import { DOCUMENT } from '@angular/common';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { FeatureFlagsService } from '@services/feature-flags.service';
import { filter } from 'rxjs/operators';

export type ThemeMode = 'light' | 'dark';
export type ThemePreference = ThemeMode | 'system';

const STORAGE_KEY = 'brightscrip:theme';
const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';
const DARK_MODE_FLAG = 'dark-mode';
const LIGHT_ONLY_ROUTE_PREFIXES = ['/actions', '/ap', '/terminal', '/pharmacy-enrollment', '/ta', '/pe'];

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly featureFlags = inject(FeatureFlagsService);
  private readonly router = inject(Router);
  private readonly darkMedia = window.matchMedia(DARK_MEDIA_QUERY);

  readonly preference = signal<ThemePreference>(this.readStoredPreference());
  private readonly systemPrefersDark = signal<boolean>(this.darkMedia.matches);
  private readonly currentUrl = signal<string>(this.router.url);
  private readonly lightOnlyRoute = computed<boolean>(() => this.isLightOnlyUrl(this.currentUrl()));

  readonly featureEnabled = this.featureFlags.select(DARK_MODE_FLAG);

  readonly theme = computed<ThemeMode>(() => {
    if (!this.featureEnabled() || this.lightOnlyRoute()) return 'light';
    const pref = this.preference();
    if (pref === 'system') return this.systemPrefersDark() ? 'dark' : 'light';
    return pref;
  });

  static clearLocalStoragePreservingTheme(): void {
    const theme = localStorage.getItem(STORAGE_KEY);
    localStorage.clear();
    if (theme) localStorage.setItem(STORAGE_KEY, theme);
  }

  constructor() {
    this.darkMedia.addEventListener('change', e => this.systemPrefersDark.set(e.matches));
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(event => this.currentUrl.set(event.urlAfterRedirects));
    effect(() => this.apply(this.theme()));
  }

  setPreference(pref: ThemePreference): void {
    if (!this.featureEnabled()) return;
    this.preference.set(pref);
    this.persist(pref);
  }

  toggle(): void {
    this.setPreference(this.theme() === 'dark' ? 'light' : 'dark');
  }

  private readStoredPreference(): ThemePreference {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light' || stored === 'system') {
      return stored;
    }
    return 'system';
  }

  private apply(mode: ThemeMode): void {
    this.document.documentElement.setAttribute('data-bs-theme', mode);
    this.document.body?.setAttribute('data-theme', mode);
  }

  private persist(pref: ThemePreference): void {
    localStorage.setItem(STORAGE_KEY, pref);
  }

  private isLightOnlyUrl(url: string): boolean {
    const normalized = url.toLowerCase();
    return LIGHT_ONLY_ROUTE_PREFIXES.some(
      prefix => normalized === prefix || normalized.startsWith(`${prefix}/`) || normalized.startsWith(`${prefix}?`),
    );
  }
}
